import { TemplateVisibility } from '@heidi/schema/ml-scribe';
import {
  useCombinedQueriesTeamGeneralInfo,
  useOrganizationInfo,
} from '@heidi/workspace/data';
import { useMemo } from 'react';
import { useTemplateFromTemplateCreationRecordId } from './useTemplateFromTemplateCreationRecordId';
import { useTemplateSharing } from './useTemplateSharing';

/**
 * Checks if a team admin is trying to edit a template from another team.
 * Returns true when:
 * - Template visibility is Team
 * - User IS a team admin (but NOT a global admin)
 * - Template's team_id does NOT match any of user's teams
 *
 * When true, the template editor should be disabled and clone workflow triggered.
 */
export const useIsTeamAdminEditingOtherTeamsTemplate = () => {
  const template = useTemplateFromTemplateCreationRecordId();
  const { isTemplateFromUserTeam } = useTemplateSharing();
  const { isOrganizationTemplateManager } = useOrganizationInfo();
  const { isTeamAdmin } = useCombinedQueriesTeamGeneralInfo();

  const isTeamAdminEditingOtherTeamsTemplate = useMemo(() => {
    // Not a team template
    if (template?.template_visibility !== TemplateVisibility.Team) {
      return false;
    }

    if (isOrganizationTemplateManager) {
      return false;
    }

    // Not a team admin - handled by useIsNonAdminEditingTeamTemplate
    if (!isTeamAdmin) {
      return false;
    }

    // Team Admin editing another team's template
    return !isTemplateFromUserTeam(template);
  }, [isTeamAdmin, isOrganizationTemplateManager, isTemplateFromUserTeam, template]);

  return { isTeamAdminEditingOtherTeamsTemplate };
};
